import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BottomNav from '../components/BottomNav';
import UserHeader from '../components/UserHeader';
import { getTargetDate } from '../utils/userSelections';
import { trackPageView } from '../utils/analytics';
import {
  getChildrenForParent,
  getStudentProgressSnapshot,
  StudentProgressSnapshot,
} from '../utils/parentStudentFirestore';
import kid1 from '../assets/kid1.webp';

const BLUE = '#3B7BF4';
const GOLD = '#F5C842';
const TEXT_DARK = '#1A1A2E';
const TEXT_GRAY = '#6B7280';
const BORDER = '#E5E7EB';
const BG = '#F5F7FB';

// Sun=0→א … Sat=6→ש
const HEBREW_DAYS = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];

const ParentProgressPage: React.FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [childSnapshot, setChildSnapshot] = useState<StudentProgressSnapshot | null>(null);
  const [childName, setChildName] = useState<string>('');
  const [loadingChild, setLoadingChild] = useState(true);

  const targetDate = getTargetDate();

  // ?childEmail= lets /connect-parent deep-link to a specific child's progress.
  const requestedChildEmail = searchParams.get('childEmail') || '';

  async function loadChildProgress(parentEmail: string) {
    try {
      if (!parentEmail) return;
      let targetEmail = requestedChildEmail;
      let targetName = '';
      if (targetEmail) {
        // Name will come from the snapshot; derive a fallback from email.
        targetName = targetEmail.split('@')[0];
      } else {
        const links = await getChildrenForParent(parentEmail);
        if (links.length === 0) return;
        targetEmail = links[0].childEmail;
        targetName = links[0].childName;
      }
      setChildName(targetName);
      const snapshot = await getStudentProgressSnapshot(targetEmail);
      if (snapshot) {
        setChildSnapshot(snapshot);
        if (snapshot.name) setChildName(snapshot.name);
      }
    } catch (e) {
      console.warn('Failed to load child progress:', e);
    } finally {
      setLoadingChild(false);
    }
  }

  useEffect(() => {
    trackPageView('ParentProgress');

    let parentEmail = '';
    try {
      const raw = localStorage.getItem('loggedInUser');
      if (raw) {
        const u = JSON.parse(raw);
        parentEmail = u.email || '';
      }
    } catch {
      /* ignore */
    }

    loadChildProgress(parentEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedChildEmail]);

  const countdown = useMemo(() => {
    if (!targetDate) return { months: 8, weeks: 3, days: 5 };
    const diff = Math.max(0, new Date(targetDate).getTime() - Date.now());
    const total = Math.floor(diff / 86400000);
    return { months: Math.floor(total / 30), weeks: Math.floor((total % 30) / 7), days: total % 7 };
  }, [targetDate]);

  const activeAgg = childSnapshot;
  const childFirstName = childName.split(' ')[0] || 'הילד';

  const progressPct = useMemo(
    () => Math.min(100, Math.round(((activeAgg?.totalVerses || 0) / 20) * 100)),
    [activeAgg],
  );
  const accuracyPct = activeAgg?.averageScore ?? 0;
  const recordings = activeAgg?.totalVerses ?? 0;
  const hours = activeAgg?.hoursLearned ?? 0;
  const level = Math.min(10, Math.floor(recordings / 10) + 1);

  // Achievements derived from snapshot data
  const achFiveParashot = recordings >= 20; // ~4 verses min per parasha
  const achHundredVerses = recordings >= 100;
  const achFiftyRec = recordings >= 50;
  const achMaster = accuracyPct >= 95 && recordings >= 100;
  const parasha = childSnapshot?.parasha ?? null;
  const parashaLabel = parasha
    ? i18n.language?.startsWith('he')
      ? parasha.hebrew
      : parasha.english
    : '—';

  const series = activeAgg?.dailySeries || [];
  const CW = 280,
    CH = 80;
  const pts =
    series.length > 1
      ? series
          .map((p, i) => `${(i / (series.length - 1)) * CW},${CH - (p.avg / 100) * CH}`)
          .join(' ')
      : '';
  const area =
    series.length > 1
      ? `M0,${CH} ` +
        series
          .map((p, i) => `L${(i / (series.length - 1)) * CW},${CH - (p.avg / 100) * CH}`)
          .join(' ') +
        ` L${CW},${CH}Z`
      : '';

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        background: BG,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <UserHeader />
      <div style={{ flexGrow: 1, overflowY: 'auto', paddingBottom: 90, direction: 'rtl' }}>
        <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* ── Student stats card ──
              RTL flex: first child → RIGHT.
              Avatar first (RIGHT), countdown second (LEFT).
          */}
          <div
            style={{
              background: 'white',
              borderRadius: 18,
              padding: 16,
              boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
              border: `1px solid ${BORDER}`,
            }}
          >
            <div style={{ fontSize: 15, fontWeight: 700, color: TEXT_DARK, marginBottom: 14 }}>
              {loadingChild
                ? 'טוען נתוני לימוד...'
                : childName
                  ? `נתוני הלימוד של ${childName}`
                  : 'נתוני לימוד'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              {/* Avatar — rightmost in RTL, full-body character */}
              <div style={{ position: 'relative', flexShrink: 0, alignSelf: 'flex-end' }}>
                <img
                  src={kid1}
                  alt=""
                  style={{ height: 100, width: 'auto', objectFit: 'contain', display: 'block' }}
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: GOLD,
                    color: TEXT_DARK,
                    fontSize: 10,
                    fontWeight: 800,
                    borderRadius: 8,
                    padding: '2px 8px',
                    whiteSpace: 'nowrap',
                  }}
                >{`רמה ${level}`}</div>
              </div>

              {/* Countdown — fills left area.
                  RTL flex: first child → rightmost.
                  Visual L→R: [months][weeks][days][ליום הגדול!]
                  DOM order: ליום הגדול! → days → weeks → months */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1 }}>
                <CountBox value={countdown.days} label="ימים" color={GOLD} textColor={TEXT_DARK} />
                <CountBox
                  value={countdown.weeks}
                  label="שבועות"
                  color={GOLD}
                  textColor={TEXT_DARK}
                />
                <CountBox
                  value={countdown.months}
                  label="חודשים"
                  color={GOLD}
                  textColor={TEXT_DARK}
                />
                <div style={{ fontSize: 12, color: TEXT_GRAY, whiteSpace: 'nowrap' }}>
                  ליום הגדול!
                </div>
              </div>
            </div>
          </div>

          {/* ── Course progress ──
              RTL flex: title first (RIGHT), percentage second (LEFT).
          */}
          <div
            style={{
              background: 'white',
              borderRadius: 18,
              padding: 16,
              boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
              border: `1px solid ${BORDER}`,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 12,
              }}
            >
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: TEXT_DARK }}>התקדמות בקורס</div>
                <div style={{ fontSize: 12, color: TEXT_GRAY, marginTop: 2 }}>
                  {childFirstName} בדרך הנכונה, המשך כך!
                </div>
              </div>
              <div style={{ fontSize: 32, fontWeight: 900, color: BLUE }}>{progressPct}%</div>
            </div>
            <div
              style={{ height: 8, background: '#E5E7EB', borderRadius: 999, overflow: 'hidden' }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${progressPct}%`,
                  background: BLUE,
                  borderRadius: 999,
                }}
              />
            </div>
          </div>

          {/* ── Weekly parasha ── */}
          <div>
            {/* Section header: title RIGHT, link LEFT */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 10,
              }}
            >
              <div style={{ fontSize: 18, fontWeight: 700, color: TEXT_DARK }}>פרשת השבוע</div>
              <button
                onClick={() => navigate('/choose-parasha')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: BLUE,
                  fontSize: 13,
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                לכל הפרשות
              </button>
            </div>

            {/* Horizontal scroll */}
            <div
              style={
                {
                  display: 'flex',
                  overflowX: 'auto',
                  gap: 12,
                  paddingBottom: 4,
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                } as React.CSSProperties
              }
            >
              {/* Active — vibrant blue */}
              <div
                style={{
                  background: '#2B4FCC',
                  borderRadius: 18,
                  padding: 16,
                  flexShrink: 0,
                  width: '84%',
                }}
              >
                {/* RTL: text first (RIGHT), icon second (LEFT) */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginBottom: 4 }}>
                      לומד עכשיו
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: 'white' }}>
                      {parashaLabel}
                    </div>
                    <div style={{ display: 'flex', gap: 16, marginTop: 6 }}>
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>
                        {recordings} פסוקים
                      </div>
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>
                        {progressPct}% הושלם
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      background: 'rgba(255,255,255,0.18)',
                      borderRadius: 12,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 22,
                      flexShrink: 0,
                    }}
                  >
                    📖
                  </div>
                </div>
                <div
                  style={{
                    height: 6,
                    background: 'rgba(255,255,255,0.25)',
                    borderRadius: 999,
                    marginTop: 16,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${progressPct}%`,
                      background: GOLD,
                      borderRadius: 999,
                    }}
                  />
                </div>
              </div>

              {/* Next parasha — peeking */}
              <div
                style={{
                  background: 'white',
                  borderRadius: 18,
                  padding: '14px 16px',
                  border: `1px solid ${BORDER}`,
                  flexShrink: 0,
                  width: '72%',
                  opacity: 0.7,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                {/* RTL: text block first (RIGHT), status second (LEFT) */}
                <div>
                  <div style={{ fontSize: 11, color: TEXT_GRAY, marginBottom: 3 }}>הבא בתור</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: TEXT_DARK }}>פרשת לך לך</div>
                </div>
                <div style={{ fontSize: 12, color: TEXT_GRAY }}>טרם התחיל</div>
              </div>
            </div>
          </div>

          {/* ── Reading accuracy ──
              RTL flex: title first (RIGHT), percentage second (LEFT).
          */}
          <div
            style={{
              background: 'white',
              borderRadius: 18,
              padding: 16,
              boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
              border: `1px solid ${BORDER}`,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                marginBottom: 12,
              }}
            >
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: TEXT_DARK }}>דיוק בקריאה</div>
                <div style={{ fontSize: 12, color: TEXT_GRAY, marginTop: 2 }}>7 ימים אחרונים</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <span style={{ fontSize: 13, color: '#22C55E', fontWeight: 700 }}>↗</span>
                <span style={{ fontSize: 32, fontWeight: 900, color: '#22C55E', lineHeight: 1 }}>
                  {accuracyPct}%
                </span>
              </div>
            </div>

            <svg
              viewBox={`0 0 ${CW} ${CH}`}
              style={{ width: '100%', height: 80, direction: 'ltr' }}
            >
              {area && <path d={area} fill="rgba(59,123,244,0.1)" />}
              {pts && (
                <polyline
                  points={pts}
                  fill="none"
                  stroke={BLUE}
                  strokeWidth={2.5}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              )}
              {series.map((p, i) => (
                <circle
                  key={i}
                  cx={(i / Math.max(series.length - 1, 1)) * CW}
                  cy={CH - (p.avg / 100) * CH}
                  r={3.5}
                  fill="white"
                  stroke={BLUE}
                  strokeWidth={2}
                />
              ))}
              {series.length > 0 && (
                <circle
                  cx={CW}
                  cy={CH - (series[series.length - 1].avg / 100) * CH}
                  r={5}
                  fill={BLUE}
                />
              )}
            </svg>

            {/* X-axis labels — LTR to match chart left→right */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 4,
                direction: 'ltr',
              }}
            >
              {series.map((p, i) => (
                <span key={i} style={{ fontSize: 11, color: TEXT_GRAY }}>
                  {HEBREW_DAYS[new Date(p.date).getDay()]}
                </span>
              ))}
            </div>
          </div>

          {/* ── Achievements ──
              RTL grid: col 1 = RIGHT, col 2 = LEFT.
              Design (right→left): trophy | circle | mic | lock
          */}
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: TEXT_DARK, marginBottom: 10 }}>
              הישגים וציוני דרך
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <AchievementTile
                icon={<span style={{ fontSize: 24 }}>🏆</span>}
                iconBg="rgba(245,200,66,0.15)"
                title="5 פרשות"
                subtitle="הושלמו בהצלחה"
                active={achFiveParashot}
              />
              <AchievementTile
                icon={<CircleIcon color={BLUE} />}
                iconBg="rgba(59,123,244,0.1)"
                title="100 פסוקים"
                subtitle="ללא שגיאות"
                active={achHundredVerses}
              />
              <AchievementTile
                icon={<MicIcon color="#A855F7" />}
                iconBg="rgba(168,85,247,0.1)"
                title="מקליט מתמיד"
                subtitle="50 הקלטות"
                active={achFiftyRec}
              />
              <AchievementTile
                icon={<span style={{ fontSize: 24 }}>🔒</span>}
                iconBg="#E5E7EB"
                title="מאסטר קריאה"
                subtitle="השלם ספר בראשית"
                active={achMaster}
              />
            </div>
          </div>

          {/* ── Practice summary ──
              RTL grid: col 1 = RIGHT → hours (blue), col 2 = LEFT → recordings (white).
          */}
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: TEXT_DARK, marginBottom: 10 }}>
              סיכום תרגול
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div
                style={{
                  background: BLUE,
                  borderRadius: 16,
                  padding: '18px 14px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <ClockIcon color="rgba(255,255,255,0.85)" />
                <div style={{ fontSize: 30, fontWeight: 900, color: 'white', lineHeight: 1 }}>
                  {hours}
                </div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>שעות לימוד</div>
              </div>
              <div
                style={{
                  background: 'white',
                  borderRadius: 16,
                  padding: '18px 14px',
                  border: `1px solid ${BORDER}`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <BarChartIcon color={TEXT_GRAY} />
                <div style={{ fontSize: 30, fontWeight: 900, color: TEXT_DARK, lineHeight: 1 }}>
                  {recordings}
                </div>
                <div style={{ fontSize: 12, color: TEXT_GRAY }}>הקלטות בוצעו</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <BottomNav />
    </div>
  );
};

// ── Sub-components ───────────────────────────────────────────────────────────

const CountBox: React.FC<{ value: number; label: string; color: string; textColor: string }> = ({
  value,
  label,
  color,
  textColor,
}) => (
  <div
    style={{
      background: color,
      color: textColor,
      borderRadius: 10,
      width: 54,
      height: 60,
      fontWeight: 800,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2,
    }}
  >
    <div style={{ fontSize: 22, lineHeight: 1 }}>{value}</div>
    <div style={{ fontSize: 10, fontWeight: 600, opacity: 0.75 }}>{label}</div>
  </div>
);

const AchievementTile: React.FC<{
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  subtitle: string;
  active: boolean;
}> = ({ icon, iconBg, title, subtitle, active }) => (
  <div
    style={{
      background: active ? 'white' : '#F3F4F6',
      borderRadius: 16,
      padding: '16px 12px',
      border: `1px solid ${active ? BORDER : '#E5E7EB'}`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 8,
      textAlign: 'center',
      opacity: active ? 1 : 0.55,
    }}
  >
    <div
      style={{
        width: 46,
        height: 46,
        borderRadius: '50%',
        background: active ? iconBg : '#E5E7EB',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {icon}
    </div>
    <div style={{ fontSize: 15, fontWeight: 800, color: active ? TEXT_DARK : TEXT_GRAY }}>
      {title}
    </div>
    <div style={{ fontSize: 11, color: TEXT_GRAY }}>{subtitle}</div>
  </div>
);

const CircleIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 3" strokeLinecap="round" />
  </svg>
);
const MicIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <rect x="9" y="2" width="6" height="12" rx="3" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v3M8 22h8" strokeLinecap="round" />
  </svg>
);
const BarChartIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <rect x="3" y="6" width="3" height="14" rx="1" />
    <rect x="10" y="10" width="3" height="10" rx="1" />
    <rect x="17" y="3" width="3" height="17" rx="1" />
  </svg>
);
const ClockIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" strokeLinecap="round" />
  </svg>
);

export default ParentProgressPage;
